import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard,
  CreditCard,
  Key,
  Settings,
  LogOut,
  ChevronRight,
  Server,
  Cpu,
  ChevronDown,
  ChevronUp,
  Shield,
  ShoppingCart,
  Menu,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';

const navigation = [
  { name: 'Overview', href: '/', icon: LayoutDashboard },
  { name: 'Apps', href: '/apps', icon: Server },
  { name: 'Buy Local GPU', href: '/buy-gpu', icon: ShoppingCart },
  { name: 'Sell GPU', href: '/sell-gpu', icon: Cpu },
];

const adminNavigation = [
  { name: 'Audit & Security', href: '/audit', icon: Shield },
];

const userMenuItems = [
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Sign out', href: null, icon: LogOut, action: 'logout' },
];

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const currentPage = navigation.find(
    (item) => item.href === location.pathname
  )?.name || userMenuItems.find((item) => item.href === location.pathname)?.name || 'Dashboard';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen]);

  const handleUserMenuItemClick = (item: typeof userMenuItems[0]) => {
    if (item.action === 'logout') {
      logout();
    } else if (item.href) {
      navigate(item.href);
    }
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-border">
        <div className="flex items-center gap-3 flex-1">
          <img src="/TY.png" alt="TY" className="w-8 h-8 rounded-lg object-contain" />
          <span className="font-semibold text-foreground">GPU Cloud</span>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="md:hidden p-1 text-foreground-secondary hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-all duration-150',
                isActive
                  ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-0.5 pl-[14px]'
                  : 'text-foreground-secondary hover:text-foreground hover:bg-background-hover'
              )}
            >
              <item.icon className="w-4 h-4" />
              {item.name}
            </NavLink>
          );
        })}

        {user?.role === 'ADMIN' && (
          <>
            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Admin</p>
            </div>
            {adminNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-all duration-150',
                    isActive
                      ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-0.5 pl-[14px]'
                      : 'text-foreground-secondary hover:text-foreground hover:bg-background-hover'
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      {/* User section with dropdown */}
      <div className="p-4 border-t border-border relative" ref={userMenuRef}>
        <button
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-background-hover rounded-md transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
            <span className="text-xs font-medium text-accent">
              {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.firstName
                ? `${user.firstName} ${user.lastName || ''}`
                : user?.email || 'Demo User'}
            </p>
            <p className="text-xs text-foreground-tertiary truncate">
              Balance: ${(user?.balance ?? 0).toFixed(2)}
            </p>
          </div>
          {isUserMenuOpen ? (
            <ChevronUp className="w-4 h-4 text-foreground-secondary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-foreground-secondary" />
          )}
        </button>

        {isUserMenuOpen && (
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-background border border-border rounded-md shadow-lg overflow-hidden">
            {userMenuItems.map((item) => {
              const isActive = item.href === location.pathname;
              return (
                <button
                  key={item.name}
                  onClick={() => handleUserMenuItemClick(item)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent'
                      : item.action === 'logout'
                      ? 'text-red-500 hover:bg-red-500/10'
                      : 'text-foreground-secondary hover:text-foreground hover:bg-background-hover'
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-border bg-background-secondary flex-col flex-shrink-0 h-screen overflow-hidden">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 bg-background-secondary border-r border-border flex flex-col z-50 animate-slide-in-left">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 md:h-16 flex items-center justify-between px-4 md:px-8 border-b border-border bg-background">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-1.5 -ml-1.5 text-foreground-secondary hover:text-foreground hover:bg-background-hover rounded-md transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-foreground-tertiary hidden sm:inline">Dashboard</span>
            <ChevronRight className="w-4 h-4 text-foreground-muted hidden sm:block" />
            <span className="text-foreground">{currentPage}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-foreground-tertiary">
              <span className="text-foreground-secondary hidden sm:inline">{user?.email}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 md:p-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
